import { PaymentMethodType } from "../payments/paymentTypes"

export enum AuthenticationAction {
    REQUIRES_PASSWORD = "REQUIRES_PASSWORD",
    AUTHENTICATION_COMPLETE = "AUTHENTICATION_COMPLETE",
    REQUIRES_ACTIVATION_CODE = "REQUIRES_ACTIVATION_CODE",
    CREATE_PASSWORD = "CREATE_PASSWORD",
    APPROVE_ACCOUNT_FROM_URL = "APPROVE_ACCOUNT_FROM_URL",
    REQUIRES_SELECT_COMPANY = "REQUIRES_SELECT_COMPANY",
}

export enum AuthenticationTokenType {
    ACTIVATION_CODE = "ACTIVATION_CODE",
    ONE_TIME_TOKEN = "ONE_TIME_TOKEN",
    DEVICE_SECRET = "DEVICE_SECRET",
}

export function toAuthenticationTokenType(value: string | null): AuthenticationTokenType | undefined {
    return Object.values(AuthenticationTokenType).find((tokenType) => tokenType === value)
}

export enum UserType {
    SYSTEM_OWNER = "SYSTEM_OWNER",
    SUPPORT = "SUPPORT",
    MERCHANT = "MERCHANT",
    COMPANY = "COMPANY",
    DEBTOR = "DEBTOR",
    USER = "USER",
    SERVICE_CENTER = "SERVICE_CENTER",
    SATELLITE_CANTEEN = "SATELLITE_CANTEEN",
    SUPPLIER = "SUPPLIER",
    OFFICE_MANAGER = "OFFICE_MANAGER",
    COMPANY_ADMIN = "COMPANY_ADMIN",
}

export enum AccountChangeType {
    EMAIL = "EMAIL",
}

export type CompanyChoice = Readonly<{
    uid: string
    name: string
    address?: {
        streetName: string
        streetNumber?: string
    }
    location?: {
        id: number
        name?: string
        address?: {
            streetName: string
            streetNumber?: string
        }
    }
    user?: {
        id: number
        username?: string
        displayName?: string
        userGroup?: {
            id: number
        }
    }
}>

export enum SelectCompanyReason {
    MULTIPLE_COMPANIES_FOR_DOMAIN = "MULTIPLE_COMPANIES_FOR_DOMAIN",
    MULTIPLE_ACCOUNTS_FOR_USERNAME = "MULTIPLE_ACCOUNTS_FOR_USERNAME",
}

export type AuthenticationUser = Readonly<{
    userType: UserType
    id: number
    companyId: number
    userGroupId?: number
    serviceCenterId?: number
    username: string
    displayInitials: string
}>

export type RequiresPasswordResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_PASSWORD
    message?: string
}>

export type AuthenticationCompleteResult = Readonly<{
    type: string
    action: AuthenticationAction.AUTHENTICATION_COMPLETE
    message?: string
}>

export type RequiresActivationCodeResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_ACTIVATION_CODE
    message?: string
}>

export type CreatePasswordResult = Readonly<{
    type: string
    action: AuthenticationAction.CREATE_PASSWORD
    message?: string
}>

export type RequiresSelectCompanyResult = Readonly<{
    type: string
    action: AuthenticationAction.REQUIRES_SELECT_COMPANY
    message?: string
}>

export type Authentication = Readonly<{
    type: string
    token: string
}>

export type RequiresPasswordResponse = Readonly<{
    authentication: Authentication
    authenticationResult: RequiresPasswordResult
}>

export type RequiresActivationCodeResponse = Readonly<{
    authentication: Authentication
    authenticationResult: RequiresActivationCodeResult
}>

export type RequiresCreatePasswordResponse = Readonly<{
    authentication: Authentication
    authenticationResult: CreatePasswordResult
    user: AuthenticationUser
}>

export type RequiresSelectCompanyResponse = Readonly<{
    authenticationResult: RequiresSelectCompanyResult
    selectCompany: {
        companies: CompanyChoice[]
        reason: SelectCompanyReason
        duplicateValue: string
    }
}>

export function isRequiresPasswordResponse(response: AuthenticationResponse): response is RequiresPasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.REQUIRES_PASSWORD
}

export function isRequiresActivationCodeResponse(
    response: AuthenticationResponse,
): response is RequiresPasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.REQUIRES_ACTIVATION_CODE
}

export function isRequiresCompanySelectResponse(
    response: AuthenticationResponse,
): response is RequiresPasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.REQUIRES_SELECT_COMPANY
}

export function isRequiresCreatePasswordResponse(
    response: AuthenticationResponse,
): response is RequiresCreatePasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.CREATE_PASSWORD
}

export type AuthenticationCompleteResponse = Readonly<{
    authenticationResult: AuthenticationCompleteResult
    authentication: Authentication
    user: AuthenticationUser
}>

export type ResetPasswordCompleteResponse = Readonly<{
    authenticationResult: AuthenticationCompleteResult
    authentication: Authentication
    user: AuthenticationUser
}>

export function isAuthenticationComplete(response: AuthenticationResponse): response is AuthenticationCompleteResponse {
    return response.authenticationResult.action === AuthenticationAction.AUTHENTICATION_COMPLETE
}

export function isCreatePassword(response: RequiresCreatePasswordResponse): response is RequiresCreatePasswordResponse {
    return response.authenticationResult.action === AuthenticationAction.CREATE_PASSWORD
}

export function isRequiresSelectCompany(response: AuthenticationResponse): response is RequiresSelectCompanyResponse {
    return response.authenticationResult.action === AuthenticationAction.REQUIRES_SELECT_COMPANY
}

export type AuthenticationResponse =
    | RequiresPasswordResponse
    | AuthenticationCompleteResponse
    | RequiresActivationCodeResponse
    | RequiresCreatePasswordResponse
    | RequiresSelectCompanyResponse

type ValidatePasswordSupplierRequest = Readonly<{
    userType: UserType.SUPPLIER
    supplierDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordMerchantRequest = Readonly<{
    userType: UserType.MERCHANT
    merchantDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordServiceCenterRequest = Readonly<{
    userType: UserType.SERVICE_CENTER
    serviceCenterDetails: {
        serviceCenterId: number
        companyId: number
    }
    password: string
}>
type ValidatePasswordCompanyRequest = Readonly<{
    userType: UserType.COMPANY
    companyDetails: {
        companyId: number
    }
    password: string
}>
type ValidatePasswordTenantRequest = Readonly<{
    userType: UserType.OFFICE_MANAGER
    officeManagerDetails: {
        companyId: number
        userGroupId: number
    }
    password: string
}>

type ValidatePasswordUserRequest = Readonly<{
    userType: UserType.USER
    companyUserDetails: {
        companyId: number
        userGroupId: number
    }
    password: string
}>

export type ValidatePasswordRequest =
    | ValidatePasswordSupplierRequest
    | ValidatePasswordMerchantRequest
    | ValidatePasswordServiceCenterRequest
    | ValidatePasswordCompanyRequest
    | ValidatePasswordTenantRequest
    | ValidatePasswordUserRequest

type ValidateSuccessResponse = Readonly<{
    validator: {
        success: true
    }
}>

type ValidateErrorResponse = Readonly<{
    validator: {
        success: false
        rules: string[]
    }
}>

export type ValidatePasswordResponse = ValidateSuccessResponse | ValidateErrorResponse

export enum LoginStatus {
    AUTHENTICATING = "AUTHENTICATING",
    LOGGED_IN = "LOGGED_IN",
}

type AuthenticatingState = Readonly<{
    status: LoginStatus.AUTHENTICATING
    username: string | undefined
    rememberMe: boolean
    response?: AuthenticationResponse
}>

type LoggedInState = Readonly<{
    status: LoginStatus.LOGGED_IN
    user: AuthenticationUser
    authToken: string
}>

export type LoginState = AuthenticatingState | LoggedInState

// ID CARD
export type ActivateIdCardBody = Readonly<{
    user: ActivateIdCardNewUser | ActivateIdCardExistUser
    cardSubscription: {
        activationToken: string
        subscriptionId: number
    }
    authentication: Authentication
}>
type ActivateIdCardNewUser = Readonly<{
    displayName: string
    mobileNumber?: string
    userGroup: {
        id: number
    }
    company: {
        id: number
    }
}>

type ActivateIdCardExistUser = Readonly<{
    uid: string
}>

export type IdCardSubscription = Readonly<{
    id: number
    name: string
    description: string
    subscriptionType: string
    paymentMethod: PaymentMethodType
}>
export type CardSubscriptionCompaniesResponse = Readonly<{
    companies: IdCardSubscriptionCompany[]
    user: IdCardSubscriptionUser
}>

export type IdCardSubscriptionUser = Readonly<{
    email: string
    displayName?: string
    uid: string
}>

export type IdCardSubscriptionCompanyUserGroup = Readonly<{
    id: number
    name: string
    displayName?: string
    emailDomainMessage?: string
    acceptedEmailDomains?: string[]
}>

export type IdCardSubscriptionCompany = Readonly<
    | {
          id: number
          name: string
          isOfficeHotel?: boolean
          userGroups: IdCardSubscriptionCompanyUserGroup[]
      }
    | {
          id: number
          name: string
          userGroup: IdCardSubscriptionCompanyUserGroup
          userGroups?: never
      }
>

export type IdCardSignupFormValues = Readonly<{
    displayName: string
    subscriptionId: number | null
    mobileNumber: string
    companyId: string | number | null
    userGroupId: string | null
}>

export type TransformedCompany = Readonly<
    | {
          id: string
          displayName: string
          isGroup: false
          companyId: number
          userGroup?: IdCardSubscriptionCompanyUserGroup
          userGroups: IdCardSubscriptionCompanyUserGroup[]
      }
    | { id: string; displayName: string; isGroup: true; companyId: number; userGroupId: number }
>

export type CompanyIdChoice = Readonly<{
    uid: string
    location?: {
        id: number
    }
    userGroup?: {
        id: number
    }
    user: {
        username: string
    }
}>
